/**
 * @Description 拓店任务详情，不含顶部操作按钮
 *   目前在拓店任务抽屉、拓店异动审批详情、机会点详情的关联任务信息栏使用
 */
import { FC, useRef, forwardRef, useImperativeHandle, useMemo, useState, useContext, useEffect } from 'react';
import V2Tabs from '@/common/components/Data/V2Tabs';
import TaskDetailApproveInfo from '@/common/components/business/ExpandStore/TaskDetailApproveInfo';
import Meet from './components/Meet';
import PointMatching from './components/PointMatching';
import styles from './index.module.less';
import { BusinessMode, TaskStatus } from '@/common/components/business/ExpandStore/ts-config';
import { isArray, isNotEmptyAny } from '@lhb/func';
import { globalAlias, globalFranchiseeAlias } from '@/common/utils/ways';
import UserInfoContext from '@/layout/context';
import CircleBasicInfo from '../CircleBasicInfo';
import FranchiseeInfo from './components/FranchiseeInfo';
import { useMethods } from '@lhb/hook';
import { getFranchiseeDetail } from '@/common/api/expandStore/franchisee';

enum TabKeys {
  changePoint = 'changePoint', // 机会点
  communication = 'communication', // 沟通记录
  franchisee = 'franchisee',
}
const DetailMain: FC<any> = forwardRef(({
  style = {},
  detail, // 拓店任务详情信息
  isApproval = false, // 是否审批详情内
  aprDetail = {}, // 审批详情
  refresh, // 主动刷新页面
  hideOperate = false, // 是否需要主动隐藏操作按钮
  hideClusterDetail = false,
}, ref) => {
  const { tenantInfo: { globalAliasItemVOs } } = useContext(UserInfoContext); // 租户信息

  const [chancePointInfo, setChancePointInfo] = useState<any>({});
  const [franDetail, setFranDetail] = useState<any>({});
  const [active, setActive] = useState<TabKeys>(TabKeys.changePoint);

  // 外部调用异动审批通过操作
  useImperativeHandle(ref, () => ({
    approveTaskChange: (cb: Function) => {
      (approveInfoRef?.current as any).approveTaskChange(cb);
    },
  }));
  const approveInfoRef = useRef(null);

  // 是否只能查看不能新增数据（审批详情或者拓店任务状态非进行中时）
  // 手动控制hideOperate
  const isView = isApproval || detail.status !== TaskStatus.Processing || hideOperate;

  const chancepointAlias = useMemo(() => {
    return globalAlias(globalAliasItemVOs);
  }, [globalAliasItemVOs]);

  /**
   * @description 业务模式决定是否显示加盟商tab
   * @return
   */
  const tabItems = useMemo(() => {
    const items: any[] = [
      {
        key: TabKeys.changePoint,
        label: `${chancepointAlias} ${chancePointInfo?.objectList?.length || 0}`,
      },
      {
        key: TabKeys.communication,
        label: `沟通记录`,
      },
    ];

    if ([BusinessMode.Franchisee, BusinessMode.Joint].includes(detail?.businessMode)) {
      items.push({
        key: TabKeys.franchisee,
        label: `${globalFranchiseeAlias(globalAliasItemVOs)}信息`,
      });
    }
    return items;
  }, [chancePointInfo, detail?.businessMode]);

  useEffect(() => {
    // 业务模式已变成直营模式，将不再显示加盟tab，但是选中tab还是加盟信息，需要额外处理下active
    if (detail?.businessMode === BusinessMode.Direct && active === TabKeys.franchisee) {
      setActive(TabKeys.changePoint);
    }
  }, [detail?.businessMode]);

  /**
   * @description 根据加盟商id刷新数据
   */
  useEffect(() => {
    const franId = detail?.standardFranchiseeId;
    // 设置了加盟模式/联营模式，却没有选择加盟商的情况下，会展示加盟信息tab但tab里面不展示数据
    if (!franId) {
      setFranDetail({});
      return;
    }
    methods.getFranDetail(franId);
  }, [detail?.standardFranchiseeId]);

  const methods = useMethods({
    getFranDetail: (id: number) => {
      getFranchiseeDetail({ id }).then((res) => {
        res && setFranDetail(res);
      });
    }
  });

  return (
    isNotEmptyAny(detail) ? <div
      className={styles.taskDetail}
      style={{ ...style }}
    >
      {/* 异动审批信息 */}
      <TaskDetailApproveInfo
        ref={approveInfoRef}
        detail={detail}
        aprDetail={aprDetail}
        isApproval={isApproval}
      />

      {/* 基本信息 */}
      <CircleBasicInfo
        isApproval={isApproval}
        refresh={refresh}
        taskDetail={detail}
        chancePointList={isArray(chancePointInfo?.objectList) ? chancePointInfo?.objectList : []}
        hideClusterDetail={hideClusterDetail}
        hideOperate={hideOperate}
      />
      <V2Tabs items={tabItems} onChange={(value: any) => setActive(value)} className='mt-8'/>

      {/* 沟通记录 */}
      {active === TabKeys.communication ? <Meet
        id={detail.id}
        isView={isView}
      /> : <></>}

      {/* 匹配点位-机会点 */}
      {active === TabKeys.changePoint ? <PointMatching
        id={detail.id}
        modelClusterId={detail?.modelClusterInfo?.modelClusterId}
        // modelClusterInfo={detail?.modelClusterInfo}
        refresh={refresh}
        chancepointAlias={chancepointAlias}
        chancePointInfo={chancePointInfo}
        setChancePointInfo={setChancePointInfo}
        hideOperate={hideOperate}
        isView={isView}/> : <></>}

      {active === TabKeys.franchisee ? <FranchiseeInfo franDetail={franDetail}/> : <></>}
    </div> : <></>
  );
});

export default DetailMain;
